import type { ReactNode } from "react";

import { GridTee } from "@/components/ui/grid-connector";
import { cn } from "@/lib/utils";

/** Outer grey-50 gutter — Figma px-20 (80px) at desktop. */
export function EstablishedPageGutter({
  children,
  className,
  dataTestId,
}: {
  children: ReactNode;
  className?: string;
  dataTestId?: string;
}) {
  return (
    <div
      className={cn("bg-grey-50 px-6 md:px-10 lg:px-20", className)}
      data-testid={dataTestId}
    >
      {children}
    </div>
  );
}

/** White bordered content column — Figma border + p-16, no rounded card. */
export function EstablishedWhiteSection({
  children,
  className,
  connectors = false,
  stack = false,
  borderBottom = false,
  dataTestId,
}: {
  children: ReactNode;
  className?: string;
  /** Top T grid connectors at outer corners (first white panel on the page grid). */
  connectors?: boolean;
  /** Collapse top border with the previous white panel. */
  stack?: boolean;
  /** Full-width grey-100 rule on the outer gutter (page rail). */
  borderBottom?: boolean;
  dataTestId?: string;
}) {
  return (
    <EstablishedPageGutter
      dataTestId={dataTestId}
      className={cn(
        "max-lg:px-0",
        borderBottom && "lg:border-grey-100 lg:border-b"
      )}
    >
      <div
        className={cn(
          "border-grey-100 bg-grey-0 relative mx-auto w-full max-w-[1280px] border px-6 py-8 md:p-16",
          "max-lg:max-w-none max-lg:border-x-0",
          stack && "-mt-px max-lg:mt-0",
          className
        )}
      >
        {connectors ? (
          <>
            <GridTee className="text-grey-950 pointer-events-none absolute -top-[0.5px] -left-[3.5px] z-10 hidden lg:block" />
            <GridTee className="text-grey-950 pointer-events-none absolute -top-[0.5px] -right-[3.5px] z-10 hidden lg:block" />
          </>
        ) : null}
        {children}
      </div>
    </EstablishedPageGutter>
  );
}

/** Full-width content within the page gutter (video carousel, channels). */
export function EstablishedContentSection({
  children,
  className,
  dataTestId,
}: {
  children: ReactNode;
  className?: string;
  dataTestId?: string;
}) {
  return (
    <EstablishedPageGutter dataTestId={dataTestId} className={className}>
      <div className="mx-auto w-full max-w-[1280px]">{children}</div>
    </EstablishedPageGutter>
  );
}
